import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandS, BrandXL } from '@sorare/core/src/atoms/typography';
import { NewSeasonLogo } from '@sorare/core/src/components/Season2026/NewSeasonLogo';
import { Season26TutorialDialog } from '@sorare/core/src/components/dialog/Season26TutorialDialog';
import { Season26TutorialStep } from '@sorare/core/src/components/dialog/Season26TutorialStep';
import {
  ANY_SPORT_HOME,
  FOOTBALL_MARKET_GO_PRO,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CompleteLimitedLineup } from './CompleteLimitedLineup';
import { Craft } from './Craft';
import { EnterPro } from './EnterPro';

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  align-items: stretch;
  width: 100%;
  background: var(--c-black);
  padding-bottom: var(--double-unit);

  @media ${tabletAndAbove} {
    flex-direction: row;
    width: auto;
    justify-content: center;
    padding-bottom: 0;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
};
export const IntroducingRoadToPro = ({ open, onClose }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const isTabletAndAbove = useIsTabletAndAbove();
  const navigate = useNavigate();

  const onClickCommon = () => {
    onClose();
    navigate(generateSportContextPath(ANY_SPORT_HOME));
  };
  return (
    <Season26TutorialDialog
      open={open}
      color="var(--c-yellow-600)"
      textColor="var(--c-black)"
      titleGroup={
        isTabletAndAbove ? (
          <Vertical gap={4} center>
            <NewSeasonLogo iconSize={3} gap="small" />
            <Vertical center>
              <BrandM color="var(--c-nd-600)">
                <FormattedMessage {...glossary.introducing} />
              </BrandM>
              <BrandXL className="text-center">
                <FormattedMessage {...glossary.roadToPro} />
              </BrandXL>
            </Vertical>
          </Vertical>
        ) : (
          <Vertical gap={2} center>
            <NewSeasonLogo iconSize={3} gap="small" />
            <Vertical center>
              <BrandS color="var(--c-nd-600)">
                <FormattedMessage {...glossary.introducing} />
              </BrandS>
              <BrandM className="text-center">
                <FormattedMessage {...glossary.roadToPro} />
              </BrandM>
            </Vertical>
          </Vertical>
        )
      }
      items={[
        <Season26TutorialStep stepNb={1} key="1">
          <Craft />
        </Season26TutorialStep>,
        <Season26TutorialStep stepNb={2} key="2">
          <CompleteLimitedLineup />
        </Season26TutorialStep>,
        <Season26TutorialStep stepNb={3} key="3">
          <EnterPro />
        </Season26TutorialStep>,
      ]}
      footer={
        <ButtonsWrapper>
          <Button size="large" color="quaternary" to={FOOTBALL_MARKET_GO_PRO}>
            <FormattedMessage
              id="IntroducingRoadToPro.buyCards"
              defaultMessage="Buy Cards now"
            />
          </Button>
          <Button size="large" color="primary" onClick={onClickCommon}>
            <FormattedMessage
              id="IntroducingRoadToPro.common"
              defaultMessage="Continue in Common"
            />
          </Button>
        </ButtonsWrapper>
      }
    />
  );
};
